import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import { usePopup } from '../context/PopupContext';
import { useTour } from '../context/TourContext';
import { isAdmin } from '../../util/auth';
import { isRouteActive } from '../../util/routeUtils';
import { ROUTES } from '../../util/routes';
import { useLogout } from '../../hooks/useLogout';
import NavBadge from '../input/NavBadge';
import { homeTourSteps, HOME_TOUR_KEY } from '../tour/tours/homeTour';
import { upcomingEventsTourSteps, UPCOMING_EVENTS_TOUR_KEY } from '../tour/tours/upcomingEventsTour';
import { eventTourSteps, EVENT_TOUR_KEY } from '../tour/tours/eventTour';
import './Sidebar.css';

const NAV_ITEMS = [
  { icon: 'home', label: 'Dashboard', to: ROUTES.HOME, exact: true, tourTarget: 'nav-home' },
  { icon: 'event', label: 'Events', to: ROUTES.UPCOMING_EVENTS, tourTarget: 'nav-events' },
  { icon: 'inbox', label: 'Inbox', to: ROUTES.REGISTRATIONS, adminOnly: true },
  { icon: 'person', label: 'Spieler', to: ROUTES.USERS, adminOnly: true },
  { icon: 'query_stats', label: 'Statistiken', to: ROUTES.STATISTICS, tourTarget: 'nav-statistics' },
];

function SidebarLabel({ children, collapsed }) {
  return (
    <AnimatePresence initial={false}>
      {!collapsed && (
        <motion.span
          className="sidebarItemLabel"
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: 'auto' }}
          exit={{ opacity: 0, width: 0 }}
          transition={{ duration: 0.18 }}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

function FooterLabel({ children, collapsed }) {
  return (
    <AnimatePresence initial={false}>
      {!collapsed && (
        <motion.span
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: 'auto' }}
          exit={{ opacity: 0, width: 0 }}
          transition={{ duration: 0.18 }}
          style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
}

function Sidebar({ collapsed, onToggle, isSubscribed, registrationsBadge }) {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { openPopup } = usePopup();
  const { startTour, openLegend, resetTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = isAdmin();
  const logout = useLogout();

  const currentPageHasTour = () => {
    const p = location.pathname;
    return p === ROUTES.HOME || p === ROUTES.UPCOMING_EVENTS || p.startsWith(ROUTES.EVENT_BASE + '/');
  };

  const handleStartTour = () => {
    const p = location.pathname;
    if (p === ROUTES.HOME) { resetTour(HOME_TOUR_KEY); startTour(HOME_TOUR_KEY, homeTourSteps); }
    else if (p === ROUTES.UPCOMING_EVENTS) { resetTour(UPCOMING_EVENTS_TOUR_KEY); startTour(UPCOMING_EVENTS_TOUR_KEY, upcomingEventsTourSteps); }
    else if (p.startsWith(ROUTES.EVENT_BASE + '/')) { resetTour(EVENT_TOUR_KEY); startTour(EVENT_TOUR_KEY, eventTourSteps); }
  };

  const visibleNavItems = NAV_ITEMS.filter(item => !item.adminOnly || admin);

  return (
    <>
      <motion.aside
        className={`sidebar${collapsed ? ' collapsed' : ''}`}
        animate={{ width: collapsed ? 60 : 220 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {/* Header */}
        <div className="sidebarHeader">
          <div className="sidebarLogo">
            {collapsed
              ? <span className="sidebarAppNameShort">AVE</span>
              : <span className="sidebarAppName">AV ENZTAL</span>
            }
          </div>
          <button className="sidebarToggleBtn" onClick={onToggle} title={collapsed ? 'Sidebar aufklappen' : 'Sidebar einklappen'}>
            <span className="material-symbols-rounded">
              {collapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebarNav">
          {visibleNavItems.map(item => (
            <Link
              key={item.to}
              to={item.to}
              className={`sidebarItem${isRouteActive(location.pathname, item) ? ' active' : ''}`}
              data-tooltip={collapsed ? item.label : undefined}
              data-tour={item.tourTarget}
            >
              <span className="sidebarItemIconWrapper">
                <span className="material-symbols-rounded sidebarItemIcon">{item.icon}</span>
                {item.to === ROUTES.REGISTRATIONS && registrationsBadge > 0 && (
                  <NavBadge count={registrationsBadge} size={13} fontSize={9} />
                )}
              </span>
              <SidebarLabel collapsed={collapsed}>{item.label}</SidebarLabel>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebarFooter" data-tour="nav-settings">
          {/* Dark mode */}
          <button
            className="sidebarFooterItem"
            onClick={toggleDarkMode}
            data-tooltip={collapsed ? (isDarkMode ? 'Light Mode' : 'Dark Mode') : undefined}
          >
            <span className="material-symbols-rounded">
              {isDarkMode ? 'light_mode' : 'dark_mode'}
            </span>
            <FooterLabel collapsed={collapsed}>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</FooterLabel>
            {!collapsed && <div className={`darkModeToggle${isDarkMode ? ' on' : ''}`} />}
          </button>

          {/* Orga Docs */}
          <button
            className="sidebarFooterItem"
            onClick={() => navigate(ROUTES.DOCS_ORGA)}
            data-tooltip={collapsed ? 'Orga-Doku' : undefined}
          >
            <span className="material-symbols-rounded">menu_book</span>
            <FooterLabel collapsed={collapsed}>Orga-Doku</FooterLabel>
          </button>

          {/* Admin Docs */}
          {admin && (
            <button
              className="sidebarFooterItem"
              onClick={() => navigate(ROUTES.DOCS_ADMIN)}
              data-tooltip={collapsed ? 'Admin-Doku' : undefined}
            >
              <span className="material-symbols-rounded">shield_person</span>
              <FooterLabel collapsed={collapsed}>Admin-Doku</FooterLabel>
            </button>
          )}

          {/* Icon Legend */}
          <button
            className="sidebarFooterItem"
            onClick={openLegend}
            data-tooltip={collapsed ? 'Icon-Legende' : undefined}
          >
            <span className="material-symbols-rounded">legend_toggle</span>
            <FooterLabel collapsed={collapsed}>Icon-Legende</FooterLabel>
          </button>

          {/* Tour */}
          {currentPageHasTour() && (
            <button
              className="sidebarFooterItem"
              onClick={handleStartTour}
              data-tooltip={collapsed ? 'Rundtour' : undefined}
            >
              <span className="material-symbols-rounded">map</span>
              <FooterLabel collapsed={collapsed}>Rundtour starten</FooterLabel>
            </button>
          )}

          {/* Push notifications */}
          <button
            className="sidebarFooterItem"
            onClick={() => openPopup('pushnotifications', 0)}
            data-tooltip={collapsed ? 'Benachrichtigungen' : undefined}
          >
            <span className="material-symbols-rounded">
              {isSubscribed ? 'notifications_active' : 'notifications'}
            </span>
            <FooterLabel collapsed={collapsed}>Benachrichtigungen</FooterLabel>
          </button>

          {/* Change Password */}
          <button
            className="sidebarFooterItem"
            onClick={() => openPopup('editpassword', 0)}
            data-tooltip={collapsed ? 'Passwort ändern' : undefined}
          >
            <span className="material-symbols-rounded">lock</span>
            <FooterLabel collapsed={collapsed}>Passwort ändern</FooterLabel>
          </button>

          <div className="sidebarDivider" />

          {/* Logout */}
          <button
            className="sidebarFooterItem danger"
            onClick={logout}
            data-tooltip={collapsed ? 'Ausloggen' : undefined}
          >
            <span className="material-symbols-rounded">logout</span>
            <FooterLabel collapsed={collapsed}>Ausloggen</FooterLabel>
          </button>
        </div>
      </motion.aside>

    </>
  );
}

export default Sidebar;
